import { Link } from "react-router-dom";
import { useNotifications, markNotificationRead } from "@/lib/queries";
import { ChefNav } from "@/components/ChefNav";
import { useQueryClient } from "@tanstack/react-query";
import { qk } from "@/lib/queries";
import { Button } from "@/components/ui/button";
import { cn, formatDateTime } from "@/lib/utils";

// Back-Order Alerts — active back-order situations for the chef's location.
// A supplier switch (fallback) is only an FYI: chefs are told, never asked.
export function BackOrderAlerts() {
  const { data } = useNotifications();
  const qc = useQueryClient();
  const notifications = data?.data ?? [];

  async function resolve(id: string) {
    await markNotificationRead(id);
    void qc.invalidateQueries({ queryKey: qk.notifications });
  }

  return (
    <div className="min-h-screen bg-canvas">
      <ChefNav notifications={notifications} />
      <div className="mx-auto max-w-5xl px-4 py-6 md:px-6">
        <h1 className="page-title">Back-Order Alerts</h1>
        <p className="page-sub">
          Items a supplier could not fulfil. When a backup supplier has it, we
          switch automatically — no approval needed.
        </p>

        {notifications.length === 0 ? (
          <div className="card p-10 text-center text-muted">
            No active back-order alerts.
          </div>
        ) : (
          <div className="space-y-4">
            {notifications.map((n) => {
              const fyi = n.type === "supplier_switched";
              return (
                <div
                  key={n.id}
                  className={cn(
                    "card border p-5",
                    fyi
                      ? "border-brand/30 bg-brand/5"
                      : "border-amber/40 bg-amber/5",
                  )}
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-medium text-ink">{n.message}</div>
                      <div className="mt-1 text-xs text-faint">
                        {formatDateTime(n.createdAt)}
                      </div>
                      {fyi && (
                        <div className="mt-2 inline-flex">
                          <span className="chip chip--cyan">
                            Supplier switched · no action needed
                          </span>
                        </div>
                      )}
                      {n.type === "manual_intervention_required" && (
                        <div className="mt-2 inline-flex">
                          <span className="chip chip--amber">
                            Manual Action Required
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-col gap-2">
                      {n.orderId && (
                        <Link to={`/app/orders/${n.orderId}`}>
                          <Button variant="outline" size="sm">
                            View Order
                          </Button>
                        </Link>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => resolve(n.id)}
                      >
                        {fyi ? "Got it" : "Mark as Resolved"}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
